import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { forkJoin, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  AttendanceWeekSummaryDto,
  AtRiskStudentRowDto,
  BehaviorCasesResponseDto,
  BillingInsightDto,
  DashboardEventDto,
  DashboardMessageDto,
  DemographicSliceDto,
  DemographicsResponseDto
} from '../models/dashboard-widgets.model';
import { DashboardData } from '../models/dashboard.model';
import { DashboardService } from '../services/dashboard.service';
import { DashboardWidgetsService } from '../services/dashboard-widgets.service';
import { LocalStorageService } from '../services/local-storage.service';
import { SchoolCalendarService } from '../services/school-calendar.service';
import { SchoolContextService } from '../services/school-context.service';
import { ScheduleService } from '../services/schedule.service';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.css']
})
export class HomeComponent implements OnInit, OnDestroy {

  readonly today = new Date();

  loading = true;
  loadError: string | null = null;
  data: DashboardData | null = null;

  calendarSelected: Date | null = null;
  private examIsoSet = new Set<string>();
  private holidayIsoSet = new Set<string>();

  /** openSIS-style widget payloads */
  attendanceWeek: AttendanceWeekSummaryDto | null = null;
  behaviorCases: BehaviorCasesResponseDto | null = null;
  billingInsight: BillingInsightDto | null = null;
  demographicsGender: DemographicsResponseDto | null = null;
  demographicsEthnicity: DemographicsResponseDto | null = null;
  highAbsenceRows: AtRiskStudentRowDto[] | null = null;
  lowestGpaRows: AtRiskStudentRowDto[] | null = null;
  dashboardMessages: DashboardMessageDto[] = [];
  upcomingEvents: DashboardEventDto[] = [];

  /** Week navigation offset from current week (0 = this week). */
  weekOffset = 0;
  weekLoading = false;

  billingFrom = '';
  billingTo = '';

  readonly piePalette = ['#3949ab', '#00897b', '#fb8c00', '#8e24aa', '#c62828', '#546e7a', '#6d4c41'];

  private destroy$ = new Subject<void>();

  constructor(
    private dashboardService: DashboardService,
    private dashboardWidgets: DashboardWidgetsService,
    private schoolCalendarService: SchoolCalendarService,
    private scheduleService: ScheduleService,
    public schoolContext: SchoolContextService,
    private localStorage: LocalStorageService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.initBillingRange();
    this.loadAll();
  }

  private initBillingRange(): void {
    const y = this.today.getFullYear();
    this.billingFrom = `${y}-01-01`;
    this.billingTo = this.toIsoDate(this.today);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private mondayIsoForOffset(offset: number): string {
    const d = new Date(this.today);
    const day = d.getDay();
    const diff = (day === 0 ? -6 : 1) - day;
    d.setDate(d.getDate() + diff + offset * 7);
    return this.toIsoDate(d);
  }

  private loadAll(): void {
    this.loading = true;
    this.loadError = null;
    const year = this.today.getFullYear();
    const weekStart = this.mondayIsoForOffset(this.weekOffset);

    forkJoin({
      dashboard: this.dashboardService.loadDashboard(),
      holidays: this.schoolCalendarService.getPublicHolidays(year),
      exams: this.scheduleService.getExams(),
      attendanceWeek: this.dashboardWidgets.getAttendanceWeek(weekStart),
      behavior: this.dashboardWidgets.getOpenBehaviorCases(),
      billing: this.dashboardWidgets.getBillingInsight(this.billingFrom, this.billingTo),
      demoGender: this.dashboardWidgets.getDemographics('GENDER'),
      demoEthnicity: this.dashboardWidgets.getDemographics('ETHNICITY'),
      highAbsence: this.dashboardWidgets.getStudentsHighAbsence(10),
      lowGpa: this.dashboardWidgets.getStudentsLowestGpa(8),
      messages: this.dashboardWidgets.getMessages(),
      events: this.dashboardWidgets.getUpcomingEvents()
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.data = res.dashboard;
          this.holidayIsoSet = new Set((res.holidays || []).map(h => (h.date || '').trim()).filter(Boolean));
          this.examIsoSet = new Set((res.exams || []).map(e => (e.date || '').trim()).filter(Boolean));
          this.attendanceWeek = res.attendanceWeek;
          this.behaviorCases = res.behavior;
          this.billingInsight = res.billing;
          this.demographicsGender = res.demoGender;
          this.demographicsEthnicity = res.demoEthnicity;
          this.highAbsenceRows = res.highAbsence;
          this.lowestGpaRows = res.lowGpa;
          this.dashboardMessages = res.messages?.items || [];
          this.upcomingEvents = res.events || [];
          this.loading = false;
        },
        error: () => {
          this.loading = false;
          this.loadError = 'Could not load dashboard. Check your connection and try again.';
        }
      });
  }

  onContextChange(): void {
    this.loadAll();
  }

  shiftWeek(delta: number): void {
    this.weekOffset += delta;
    this.weekLoading = true;
    const weekStart = this.mondayIsoForOffset(this.weekOffset);
    this.dashboardWidgets.getAttendanceWeek(weekStart).pipe(takeUntil(this.destroy$)).subscribe(
      w => {
        this.attendanceWeek = w;
        this.weekLoading = false;
      },
      () => {
        this.weekLoading = false;
      }
    );
  }

  /** Normalize billing bar width vs max of the three totals. */
  billingBarPct(part: number, b: BillingInsightDto): number {
    const max = Math.max(b.collectedYtd || 0, b.overdue || 0, b.toBeCollected || 0, 1);
    return ((part || 0) / max) * 100;
  }

  weekTitle(): string {
    if (this.attendanceWeek?.weekStart && this.attendanceWeek?.weekEnd) {
      const a = new Date(this.attendanceWeek.weekStart);
      const b = new Date(this.attendanceWeek.weekEnd);
      return `${a.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${b.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;
    }
    return 'This week';
  }

  calendarDateClass = (d: Date): string => {
    const iso = this.toIsoDate(d);
    if (this.examIsoSet.has(iso)) {
      return 'cal-exam';
    }
    if (this.holidayIsoSet.has(iso)) {
      return 'cal-public-holiday';
    }
    if (d.getDay() === 6 && this.isSecondSaturday(d)) {
      return 'cal-second-saturday';
    }
    if (d.getDay() === 0) {
      return 'cal-sunday';
    }
    return '';
  };

  private toIsoDate(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private isSecondSaturday(d: Date): boolean {
    if (d.getDay() !== 6) {
      return false;
    }
    const dom = d.getDate();
    return dom >= 8 && dom <= 14;
  }

  dayStackTotal(day: { present: number; absent: number; halfDay: number }): number {
    const t = (day.present || 0) + (day.absent || 0) + (day.halfDay || 0);
    return t > 0 ? t : 1;
  }

  stackPct(day: { present: number; absent: number; halfDay: number }, key: 'present' | 'absent' | 'halfDay'): number {
    const t = this.dayStackTotal(day);
    return ((day[key] || 0) / t) * 100;
  }

  pieGradient(slices: DemographicSliceDto[] | undefined): string {
    if (!slices || !slices.length) {
      return '#e0e0e0';
    }
    const total = slices.reduce((s, x) => s + (x.count || 0), 0) || 1;
    let acc = 0;
    const parts: string[] = [];
    slices.forEach((sl, i) => {
      const pct = ((sl.count || 0) / total) * 100;
      const col = sl.color || this.piePalette[i % this.piePalette.length];
      const start = acc;
      acc += pct;
      parts.push(`${col} ${start}% ${acc}%`);
    });
    return `conic-gradient(${parts.join(', ')})`;
  }

  greetingName(): string {
    const u = this.localStorage.getUser();
    if (u && typeof u === 'object') {
      const username = (u as { username?: string }).username;
      if (username) {
        return username;
      }
    }
    return 'there';
  }

  schoolHeadline(): string {
    return this.data?.tenantDisplayName?.trim() || this.localStorage.getSchoolDisplayName() || '';
  }

  roleLabel(): string {
    const r = this.localStorage.getRole();
    if (!r) {
      return '';
    }
    return r.replace(/^ROLE_/, '').replace(/_/g, ' ').toLowerCase();
  }

  attendanceHeadline(): string {
    const a = this.data?.attendanceToday;
    if (a?.presentPercent != null) {
      return `${Math.round(a.presentPercent)}%`;
    }
    return '—';
  }

  attendanceSubtext(): string {
    const a = this.data?.attendanceToday;
    if (a == null) {
      return 'Connect GET /attendance/summary/today to show live data.';
    }
    const parts: string[] = [];
    if (a.recordedClasses != null) {
      parts.push(`${a.recordedClasses} class(es) recorded`);
    }
    if (a.presentCount != null || a.absentCount != null) {
      parts.push(`${a.presentCount ?? '—'} present · ${a.absentCount ?? '—'} absent`);
    }
    return parts.length ? parts.join(' · ') : 'Today’s attendance';
  }

  retry(): void {
    this.loadAll();
  }

  go(path: string): void {
    this.router.navigateByUrl(path);
  }

  isAdmin(): boolean {
    return this.localStorage.isAdmin();
  }

  isTeacher(): boolean {
    return this.localStorage.isTeacher();
  }

  formatMsgDate(iso: string): string {
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  }
}
